import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";

import { cn } from "../ui/utils";

/**
 * Props for DataTableStateRow.
 *
 * Renders a single `<tr>` placeholder for a data table body expressing either
 * a loading or empty state. Returns `null` when both flags are false so callers
 * can still render real rows in the same tbody.
 */
export type DataTableStateRowProps = {
  /** Number of columns the placeholder row should span. */
  colSpan: number;
  /** Whether the table is currently fetching data. */
  loading: boolean;
  /** Whether the fetched dataset is empty (ignored while loading). */
  isEmpty: boolean;
  /** Optional icon shown above the empty-state title. */
  emptyIcon?: ReactNode;
  /** Optional empty-state title. Defaults to `common.noRecords`. */
  emptyTitle?: string;
  /** Optional secondary hint shown under the empty title. */
  emptyHint?: string;
  /** Optional loading label. Defaults to `common.loading`. */
  loadingLabel?: string;
  /** Extra className applied to the `<td>` wrapper. */
  className?: string;
};

/**
 * Unified loading / empty-state row for data tables.
 *
 * Use together with `TablePaginationFooter` so every list page shares a
 * consistent placeholder. See `docs/数据表格组件指南.md`.
 */
export function DataTableStateRow({
  colSpan,
  loading,
  isEmpty,
  emptyIcon,
  emptyTitle,
  emptyHint,
  loadingLabel,
  className,
}: DataTableStateRowProps) {
  const { t } = useTranslation();

  if (loading) {
    return (
      <tr>
        <td
          colSpan={colSpan}
          className={cn("px-6 py-16 text-center", className)}
        >
          <div className="flex items-center justify-center gap-2 text-slate-500 dark:text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>{loadingLabel ?? t("common.loading")}</span>
          </div>
        </td>
      </tr>
    );
  }

  if (isEmpty) {
    return (
      <tr>
        <td
          colSpan={colSpan}
          className={cn("px-6 py-16 text-center", className)}
        >
          <div className="text-center">
            {emptyIcon ? (
              <div className="mx-auto h-16 w-16 bg-white dark:bg-[#D2F0E1]/5 rounded-full flex items-center justify-center border border-slate-200 dark:border-slate-800 mb-4 text-slate-400 dark:text-slate-400">
                {emptyIcon}
              </div>
            ) : null}
            <h3 className="mt-2 text-base font-bold text-slate-900 dark:text-white">
              {emptyTitle ?? t("common.noRecords")}
            </h3>
            {emptyHint ? (
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                {emptyHint}
              </p>
            ) : null}
          </div>
        </td>
      </tr>
    );
  }

  return null;
}
